import { Version } from '@restofrit/structures';
import { Request, RequestMiddleware, Server } from '@simonbackx/simple-networking';

export function sleep(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Shared reveal-gesture helper for the hidden entry point into the
 * environment switcher (Settings' version footer — see SettingsView.vue).
 * Counts rapid taps and reports true exactly once `target` is reached; the
 * count resets whenever two taps are more than `windowMs` apart, so idly
 * tapping a screen over a session can never trigger it by accident — only a
 * deliberate fast burst does. This is a UX deterrent against casual/curious
 * users, not a security boundary — actual admission is enforced server-side
 * (see EnvironmentSession.ts / SetSessionEnvironmentEndpoint), which is what
 * matters, since the app bundle is unminified (no Terser configured in
 * webpack.config.js) and this logic is trivially readable either way.
 */
export class RapidTapGate {
    private count = 0
    private lastTapAt = 0

    constructor(private target = 6, private windowMs = 600) {}

    /** Register a tap. Returns true exactly once — the moment the target is reached. */
    tap(): boolean {
        const now = Date.now()
        if (now - this.lastTapAt > this.windowMs) {
            this.count = 0
        }
        this.lastTapAt = now
        this.count++
        return this.count === this.target
    }
}

export class NetworkManagerStatic implements RequestMiddleware {
    displayedErrorMessage = false

    /**
     * Normal, non authenticated requests.
     *
     * Always the one public API_URL baked in at build time — there is no
     * runtime override anymore. Which backend actually serves a request is
     * decided entirely server-side, from the authenticated token's
     * environment column (see backend/src/helpers/EnvironmentProxyMiddleware.ts),
     * never by anything the client sends or stores.
     */
    get server() {
        const url = process.env.API_URL
            ? process.env.API_URL
            : (process.env.API_PORT ? (window.location.protocol + "//" + window.location.hostname + ":" + process.env.API_PORT) : "")
        const server = new Server(url)
        server.middlewares.push(this)
        return server
    }

    onBeforeRequest(request: Request<any>): Promise<void> {
        request.version = Version;
        (request as any).retryCount = ((request as any).retryCount ?? 0) + 1
        return Promise.resolve()
    }

    async shouldRetryNetworkError(request: Request<any>, error: Error): Promise<boolean> {
        console.error("network error", error)
        await sleep(Math.min(((request as any).retryCount ?? 0) * 1000, 7000));

        if ((request as any).retryCount > 1 && !this.displayedErrorMessage) {
            this.displayedErrorMessage = true;
            // todo: present error message
        }
        return Promise.resolve(true);
    }

    async shouldRetryServerError(request: Request<any>, response: XMLHttpRequest, error: Error): Promise<boolean> {
        console.error("server error", error)
        console.error(error)
        console.error(response)
        return Promise.resolve(false);
    }

    onNetworkResponse(request: Request<any>, response: XMLHttpRequest) {
        // todo: hide network error message
        this.displayedErrorMessage = false;
    }
}

export const NetworkManager = new NetworkManagerStatic()